import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Users, Award, Globe, Heart } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";

const About = () => {
  const { t } = useLanguage();
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Shikwaru Adventures",
      "foundingDate": "2010",
      "description": "African safari tour operator with over 14 years of experience",
      "areaServed": ["Namibia", "Botswana", "Zimbabwe"],
      "knowsAbout": ["Wildlife Safari", "African Tourism", "Conservation"]
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="About Shikwaru Adventures | 20+ Years African Safari Expertise & Owner-Run Excellence"
        description="Family-owned African safari company with 20+ years experience. Expert multilingual guides passionate about wildlife, culture & heritage. Sustainable tourism supporting local communities. SATSA, FGASA & SafariBookings certified. Customized tours in Namibia, Botswana, Zimbabwe & South Africa."
        keywords="about Shikwaru Adventures, owner-run safari company, 20 years safari experience, African tour operator, sustainable safari tourism, SATSA certified, FGASA guides, multilingual safari guides, responsible tourism Africa, family safari business, certified safari operator"
        structuredData={structuredData}
        canonical="/about"
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-b from-secondary/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">{t("about.title")}</h1>
            <p className="text-xl text-muted-foreground">
              {t("about.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">{t("about.ourStory")}</h2>
            <div className="space-y-4 text-muted-foreground text-lg">
              <p>{t("about.story1")}</p>
              <p>{t("about.story2")}</p>
              <p>{t("about.story3")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-secondary/20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">{t("about.whyChoose")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Users className="w-12 h-12 text-primary" />,
                title: t("about.expertGuides"),
                description: t("about.expertGuidesDesc")
              },
              {
                icon: <Award className="w-12 h-12 text-primary" />,
                title: t("about.experience"),
                description: t("about.experienceDesc")
              },
              {
                icon: <Globe className="w-12 h-12 text-primary" />,
                title: t("about.tailorMade"),
                description: t("about.tailorMadeDesc")
              },
              {
                icon: <Heart className="w-12 h-12 text-primary" />,
                title: t("about.sustainable"),
                description: t("about.sustainableDesc")
              },
            ].map((value, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="flex justify-center mb-4">{value.icon}</div>
                  <CardTitle>{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">{t("about.whatWeOffer")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: t("about.campingTours"),
                  description: t("about.campingDesc")
                },
                {
                  title: t("about.midrangeTours"),
                  description: t("about.midrangeDesc")
                },
                {
                  title: t("about.luxurySafaris"),
                  description: t("about.luxuryDesc")
                },
              ].map((offer, index) => (
                <Card key={index} className="hover:shadow-lg transition-all">
                  <CardHeader>
                    <CardTitle className="text-center">{offer.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-center">{offer.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
